/**
 * Desktop Discover LIST row (lane D2). INVENTORY-DESKTOP §3.3;
 * `design/Games Tonight.dc.html` lines 163-175.
 *
 * This layout is DESKTOP-ONLY — mobile's list row is a 96px thumb beside a
 * three-line stack, desktop's is a six-column table-ish line that wraps:
 *
 *   118px thumb · flex:1 min-width:170 name/dev · min-width:190 meta ·
 *   min-width:80 price · min-width:160 review · 24×24 add
 *
 * NO HOVER TILT (§6.4): the tilt is applied to gallery tiles and board capsules
 * only. List rows are a plain `cursor:pointer` surface with no `:hover` rule at
 * all — the prototype gives them none.
 *
 * Every field is already on `TileVM` (`ea`, `demo`, `peakTxt`, `playersTxt`,
 * `dateTxt`, `hasOld`, `oldTxt`), so this row needed no new selector.
 *
 * DESKTOP-ONLY strings here:
 *   the two thumbnail corner badges `EARLY ACCESS` (--acc) / `FREE DEMO`
 *   (--acc2), pixel 5px, and the meta line
 *   `{playersTxt} · {dateTxt} · PEAK {peakTxt}`.
 */

import { CoverArt } from '../../components/CoverArt.tsx';
import { useArt } from '../../components/useArt.ts';
import { useQd } from '../../store/index.ts';
import { NO_STEAM_TIP, quickAddBlocked } from '../../store/selectors.3c.ts';
import { ADD_BLOCKED } from './ResultTile.tsx';
import type { ResultVM } from './ResultTile.tsx';
import type { CSSProperties } from 'react';

/** the two 5px pixel badges over the 460×215 thumb */
const BADGE: CSSProperties = {
  position: 'absolute',
  left: 4,
  fontFamily: 'var(--gt-font-pixel)',
  fontSize: 5,
  background: 'var(--gt-field)',
  padding: '2px 3px',
};

export function ResultRow({ g }: { g: ResultVM }): JSX.Element {
  const openGame = useQd((s) => s.openGame);
  const openAdd = useQd((s) => s.openAdd);
  const art = useArt(g.id + 'w', g.hdrCands);

  const blocked = quickAddBlocked(g.id) && !g.inList;
  const tone = blocked ? ADD_BLOCKED : { bd: g.addBd, clr: g.addClr, bg: g.addBg };

  return (
    <div
      onClick={() => openGame(g.id)}
      style={{
        display: 'flex',
        gap: 14,
        alignItems: 'center',
        padding: '9px 12px',
        border: '1px solid var(--gt-line)',
        background: 'var(--gt-card)',
        cursor: 'pointer',
        flexWrap: 'wrap',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: 118,
          aspectRatio: '460/215',
          background: g.fall,
          overflow: 'hidden',
          flex: 'none',
        }}
      >
        <CoverArt style={{ position: 'absolute', inset: 0 }} fs={10} ini={g.ini} hasCover={art.hasCover} cover={art.cover} />
        {g.ea ? <div style={{ ...BADGE, top: 4, border: '1px solid var(--acc)', color: 'var(--acc)' }}>EARLY ACCESS</div> : null}
        {g.demo ? (
          <div style={{ ...BADGE, bottom: 4, border: '1px solid var(--acc2)', color: 'var(--acc2)' }}>FREE DEMO</div>
        ) : null}
      </div>

      <div style={{ flex: 1, minWidth: 170, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--gt-text)' }}>{g.name}</div>
        <div style={{ fontSize: 11, color: 'var(--gt-text-5)' }}>{g.dev}</div>
        {/* Why the feed chose this row. In the list view it sits under the
            developer rather than in the meta column, which is a fixed-width
            strip of facts about the GAME — this is a fact about the ranking. */}
        {g.hasWhy ? (
          <div style={{ fontFamily: 'var(--gt-font-mono)', fontSize: 9.5, color: 'var(--acc)', letterSpacing: '.04em' }}>
            {g.whyTxt}
          </div>
        ) : null}
      </div>

      <div style={{ minWidth: 190, fontFamily: 'var(--gt-font-mono)', fontSize: 10.5, color: 'var(--gt-text-5)' }}>
        {g.playersTxt} · {g.dateTxt} · PEAK {g.peakTxt}
      </div>

      <div
        style={{
          minWidth: 80,
          fontFamily: 'var(--gt-font-mono)',
          fontSize: 11.5,
          color: 'var(--gt-text)',
          display: 'flex',
          gap: 6,
          alignItems: 'baseline',
        }}
      >
        {g.hasOld ? (
          <span style={{ textDecoration: 'line-through', color: 'var(--gt-muted-2)', fontSize: 9.5 }}>{g.oldTxt}</span>
        ) : null}
        <span>{g.priceTxt}</span>
      </div>

      <div
        style={{
          minWidth: 160,
          fontFamily: 'var(--gt-font-mono)',
          fontSize: 9,
          color: g.revClr,
          display: 'flex',
          gap: 4,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
          ▮ {g.revLbl}
        </span>
        {g.revPct ? <span style={{ flex: 'none' }}>{g.revPct}</span> : null}
      </div>

      <span
        data-tip={blocked ? NO_STEAM_TIP : g.addTip}
        onClick={(ev) => {
          ev.stopPropagation();
          if (blocked) return;
          openAdd(g.id);
        }}
        style={{
          width: 24,
          height: 24,
          border: '1px solid ' + tone.bd,
          color: tone.clr,
          background: tone.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingBottom: 1.5,
          boxSizing: 'border-box',
          fontFamily: 'var(--gt-font-mono)',
          fontSize: 14,
          fontWeight: 700,
          lineHeight: 1,
          flex: 'none',
          cursor: blocked ? 'not-allowed' : 'pointer',
        }}
      >
        {g.addG}
      </span>
    </div>
  );
}

export default ResultRow;
